<div id="maintanance-criteria" class="container">
  <div class="maintenance-page-layout">
    @if (isScreenForSinglePatient) {
      <section class="maintenance-page-section">
        <div class="maintenance-page-section-header maintenance-page-section-header-row">
          <h5 class="mb-1">Current Configuration</h5>
          @if (hasLegacyMaintenanceCriteria) {
            <button
              type="button"
              class="btn btn-link maintenance-page-info-button"
              (click)="toggleLegacyConfigurationDetails()"
            >
              <i class="pi pi-info-circle"></i>
              {{ showLegacyConfigurationDetails ? 'Hide previous config' : 'View previous config' }}
            </button>
          }
        </div>

        <div class="maintenance-page-callout">
          <div class="maintenance-page-callout-copy">
            <div class="maintenance-page-kicker">{{ pageModeLabel }}</div>
            <p class="maintenance-page-summary mb-0">
              {{ pageSummary }}
            </p>
            <p class="maintenance-page-link-row mb-0">
              Review
              <a [routerLink]="['/settings', 'maintanance-criteria']"
                >general maintenance criteria</a
              >
              here.
            </p>
          </div>

          @if (!hasCustomMaintananceCriteria) {
            <button
              type="button"
              (click)="enableCustomMaintananceCriteria()"
              class="btn btn-secondary"
            >
              Enable Patient-Specific Criteria
            </button>
          }
        </div>

        @if (hasLegacyMaintenanceCriteria && showLegacyConfigurationDetails) {
          <div class="maintenance-page-legacy-card mt-3">
            <h6 class="mb-3">Previous Custom Configuration</h6>

            <div class="maintenance-page-legacy-subtitle">Initial Level</div>
            <div class="maintenance-page-legacy-settings">
              <div>
                <strong>Targets mastered in treatment go to:</strong>
                {{ getLegacyInitialTransitionLabel(legacyMaintananceCriteriaSettings?.MaintenanceLevelIDWhenFromAquisition) }}
              </div>
              <div>
                <strong>Targets mastered in baseline go to:</strong>
                {{ getLegacyInitialTransitionLabel(legacyMaintananceCriteriaSettings?.MaintenanceLevelIDWhenFromProbe) }}
              </div>
            </div>

            <div class="maintenance-page-legacy-subtitle">Maintenance Criteria</div>
            @if (displayLegacyMaintenanceCriterias.length === 0) {
              <div class="maintenance-page-legacy-row">
                <div>No legacy maintenance criteria details were returned for this patient.</div>
              </div>
            } @else {
              @for (legacyCriteria of displayLegacyMaintenanceCriterias; track getLegacyCriteriaTrackKey($index, legacyCriteria)) {
                <div class="maintenance-page-legacy-row">
                  <div class="maintenance-page-legacy-level">
                    {{ getLegacyCriteriaLevelLabel(legacyCriteria) }}
                  </div>
                  <div class="maintenance-page-legacy-transition-grid">
                    <div class="maintenance-page-legacy-transition-row">
                      <div class="maintenance-page-legacy-transition-cell">
                        <strong>Promotion:</strong>
                        {{ formatThreshold(legacyCriteria.PromotionNeeded, legacyCriteria.PromotionTotal) }}
                      </div>
                      <div class="maintenance-page-legacy-transition-cell">
                        <strong>Target status:</strong>
                        {{ getLegacyTransitionStatusLabel(legacyCriteria, true) }}
                      </div>
                      <div class="maintenance-page-legacy-transition-cell">
                        <strong>Maintenance level:</strong>
                        {{ getLegacyTransitionMaintenanceLevelLabel(legacyCriteria, true) }}
                      </div>
                    </div>
                    <div class="maintenance-page-legacy-transition-row">
                      <div class="maintenance-page-legacy-transition-cell">
                        <strong>Demotion:</strong>
                        {{ formatThreshold(legacyCriteria.DemotionNeeded, legacyCriteria.DemotionTotal) }}
                      </div>
                      <div class="maintenance-page-legacy-transition-cell">
                        <strong>Target status:</strong>
                        {{ getLegacyTransitionStatusLabel(legacyCriteria, false) }}
                      </div>
                      <div class="maintenance-page-legacy-transition-cell">
                        <strong>Maintenance level:</strong>
                        {{ getLegacyTransitionMaintenanceLevelLabel(legacyCriteria, false) }}
                      </div>
                    </div>
                  </div>
                </div>
              }
            }
          </div>
        }
      </section>
    }

    <section class="maintenance-page-section">
      <div class="maintenance-page-section-header">
        <h5 class="mb-1">Initial Level</h5>
      </div>

      <div class="maintenance-page-form-card">
        <div class="maintenance-page-form-group">
          <label for="whatMaintananceLevelMastered"
            >Targets mastered in treatment go to</label
          >
          <p-dropdown
            id="whatMaintananceLevelMastered"
            name="whatMaintananceLevelMastered"
            styleClass="w-100"
            [(ngModel)]="maintananceCriteriaSettings.MaintenanceLevelIDWhenFromAquisition"
            [options]="initialTransitionOptions"
            optionLabel="label"
            optionValue="value"
            [disabled]="!hasCustomMaintananceCriteria && isScreenForSinglePatient"
          />
          <p class="maintenance-page-hint mb-0">
            {{ acquisitionLevelHint }}
          </p>
        </div>

        <div class="maintenance-page-form-group mb-0">
          <label for="whatMaintananceLevelProbe"
            >Targets mastered in baseline go to</label
          >
          <p-dropdown
            id="whatMaintananceLevelProbe"
            name="whatMaintananceLevelProbe"
            styleClass="w-100"
            [(ngModel)]="maintananceCriteriaSettings.MaintenanceLevelIDWhenFromProbe"
            [options]="initialTransitionOptions"
            optionLabel="label"
            optionValue="value"
            [disabled]="!hasCustomMaintananceCriteria && isScreenForSinglePatient"
          />
          <p class="maintenance-page-hint mb-0">
            {{ probeLevelHint }}
          </p>
        </div>
      </div>
    </section>

    <section class="maintenance-page-section">
      <div class="maintenance-page-section-header">
        <h5 class="mb-1">Maintenance Criteria</h5>
        <p class="maintenance-page-hint mb-0">
          {{ levelRulesHint }}
        </p>
      </div>

      @if (loadingMaintananceCriteria) {
        <app-loading-indicator />
      } @else {
        @for (maintenanceCriteria of displayMaintenanceCriterias; track maintenanceCriteria.MaintenanceLevelID) {
          <div class="mb-3 maintenance-level-card">
            <div class="maintenance-level-header">
              <h5 class="mb-0">{{ getMaintananceLevelName(maintenanceCriteria.MaintenanceLevelID) }}</h5>
            </div>
            <div class="maintenance-level-grid">
              <div class="maintenance-rule-panel maintenance-rule-panel-promotion">
                <h6 class="maintenance-rule-title">Promotion</h6>

                <div class="maintenance-rule-field">
                  <label for="PromotionNeeded">Promotion Threshold</label>
                  <div class="maintenance-threshold-row">
                    <input
                      type="number"
                      id="PromotionNeeded"
                      name="PromotionNeeded"
                      class="form-control inline-input"
                      [(ngModel)]="maintenanceCriteria.PromotionNeeded"
                      [disabled]="isCriteriaReadOnly(maintenanceCriteria)"
                    />
                    <span class="maintenance-threshold-separator">out of</span>
                    <input
                      type="number"
                      id="PromotionTotal"
                      name="PromotionTotal"
                      class="form-control inline-input"
                      [(ngModel)]="maintenanceCriteria.PromotionTotal"
                      [disabled]="isCriteriaReadOnly(maintenanceCriteria)"
                    />
                  </div>
                </div>

                <div class="maintenance-rule-field">
                  <label for="PromoteTo">Promote to</label>
                  <p-dropdown
                    id="PromoteTo"
                    name="PromoteTo"
                    styleClass="w-100"
                    [ngModel]="getPromotionTransitionValue(maintenanceCriteria)"
                    (onChange)="updatePromotionTransition(maintenanceCriteria, $event.value)"
                    [options]="getPromotionTransitionOptions(maintenanceCriteria)"
                    optionLabel="label"
                    optionValue="value"
                    [disabled]="isCriteriaReadOnly(maintenanceCriteria)"
                  />
                </div>
              </div>

              <div class="maintenance-rule-panel maintenance-rule-panel-demotion">
                <h6 class="maintenance-rule-title">Demotion</h6>

                <div class="maintenance-rule-field">
                  <label for="DemotionNeeded">Demotion Threshold</label>
                  <div class="maintenance-threshold-row">
                    <input
                      type="number"
                      id="DemotionNeeded"
                      name="DemotionNeeded"
                      class="form-control inline-input"
                      [(ngModel)]="maintenanceCriteria.DemotionNeeded"
                      [disabled]="isCriteriaReadOnly(maintenanceCriteria)"
                    />
                    <span class="maintenance-threshold-separator">out of</span>
                    <input
                      type="number"
                      id="DemotionTotal"
                      name="DemotionTotal"
                      class="form-control inline-input"
                      [(ngModel)]="maintenanceCriteria.DemotionTotal"
                      [disabled]="isCriteriaReadOnly(maintenanceCriteria)"
                    />
                  </div>
                </div>

                <div class="maintenance-rule-field">
                  <label for="DemoteTo">Demote to</label>
                  <p-dropdown
                    id="DemoteTo"
                    name="DemoteTo"
                    styleClass="w-100"
                    [ngModel]="getDemotionTransitionValue(maintenanceCriteria)"
                    (onChange)="updateDemotionTransition(maintenanceCriteria, $event.value)"
                    [options]="getDemotionTransitionOptions(maintenanceCriteria)"
                    optionLabel="label"
                    optionValue="value"
                    [disabled]="isCriteriaReadOnly(maintenanceCriteria)"
                  />
                </div>
              </div>
            </div>
          </div>
        }
      }

      @if (hasCustomMaintananceCriteria || !isScreenForSinglePatient) {
        <div class="actions d-flex gap-2 w-100 mt-3 justify-content-between">
          <button type="button" (click)="save()" class="btn btn-primary">Save</button>
          @if (isScreenForSinglePatient && shouldShowResetToGeneralSettings) {
            <button type="button" (click)="resetToGeneralSettings()" class="btn btn-secondary">Reset to General Settings</button>
          }
        </div>
      }
    </section>
  </div>
</div>